// Handle Twitch OAuth errors on the route surface

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

type UseRouteTwitchErrorParams = {
  /**
   * When true (default), the error is surfaced as a `toast.error(...)`.
   * Pass false from surfaces that render the error inline themselves
   * (e.g. `ConnectionsDialog`'s Alert, `Player`'s form error line) so we
   * don't double-display.
   */
  toastOnError?: boolean;
};

type UseRouteTwitchErrorResult = {
  error: string | null;
  code: string | null;
  clearError: () => void;
};

/**
 * Reads a Twitch OAuth error from the URL's `error` query param, maps it to
 * a user-friendly message, and clears the param. Auto-dismisses after 10s.
 * By default, toasts the error — callers that render it inline pass
 * `{ toastOnError: false }`.
 */
export function useRouteTwitchError({
  toastOnError = true,
}: UseRouteTwitchErrorParams = {}): UseRouteTwitchErrorResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (!errorParam) return;

    const errorCode = decodeURIComponent(errorParam);
    const errorMessage = mapTwitchErrorCode(errorCode);
    setCode(errorCode);
    setError(errorMessage);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('error');
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!error || !toastOnError) return;
    toast.error(error);
    setError(null);
  }, [error, toastOnError]);

  useEffect(() => {
    if (!error && !code) return;

    const timeout = setTimeout(() => {
      setError(null);
      setCode(null);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [code, error]);

  const clearError = () => {
    setError(null);
    setCode(null);
  };

  return { error, code, clearError };
}

function mapTwitchErrorCode(code: string): string {
  switch (code) {
    case 'access_denied':
      return 'Twitch permissions were not granted.';
    case 'authentication_failed':
      return 'Failed to authenticate with Twitch. Please try again.';
    case 'email_required':
      return 'Twitch did not provide an email address for this account. Try email login instead.';
    case 'twitch_already_linked':
      return 'This Twitch account is already linked to another account.';
    case 'twitch_email_conflict':
      return 'That Twitch email is already connected to a different account.';
    case 'authentication_required':
      return 'You need to be logged in before connecting Twitch.';
    case 'no_twitch_auth':
      return 'Connect Twitch to use this feature.';
    case 'twitch_account_mismatch':
      return 'That Twitch account does not match your existing connection.';
    case 'missing_bot_scopes':
      return 'Poppy was not given permission to post in your chat. Try again and approve every box.';
    case 'missing_room_code':
      return 'We could not determine which room to return to.';
    default:
      return `Error: ${code}`;
  }
}
